import { ReactNode, Fragment, useContext, useCallback, useState } from 'react';
import Head from 'next/head';
import Script from 'next/script';
import Header from 'components/_global/header';
import Footer from 'components/_global/footer';
import CookieConsent from 'components/_global/cookie-consent';
import { css } from '@emotion/react';
import LayoutProviders from './providers';
import config from '../../../util/load-config';
import { Flex } from 'components/box';
import { StickyMessageInterface, PageLayoutProps } from 'types/types';
import {
  NAVIGATION_BAR_HEIGHT_MOBILE,
  NAVIGATION_BAR_HEIGHT_TABLET,
} from 'components/_global/navigation-bar';
import dynamic from 'next/dynamic';
import resourceUrl from '../../../util/make-url';
import {
  useAddGlobalStatus,
  useNavigationBarActive,
  useVodapayActive,
} from 'hooks/app';
import ErrorBoundary from 'components/_global/error-boundary';
import { ConvertExperiencesWidget } from 'components/_global/convert-experiences';
import { useGoogleTagInfo } from 'hooks/customer';
import MoreProductsFooter from 'components/_shared/widgets/more-products-footer';
import SideDrawerOverlay from '../side-drawer/overlay';
import ApolloClientOptionsContext from 'contexts/apollo-client-options';
import { GoogleTagManagerSettings } from 'types/interface';
import { GoogleTagInfo } from 'types/third-party/gtm';
import GTMAlwaysOnListener from 'components/_global/layout/gtm-always-on-listener';
import BloomreachInit from 'components/_global/bloomreach';
import { GTAG_INITIALIZED } from 'constants/gtm';
import { useRouter } from 'next/router';
import SeoHeader from 'components/_global/layout/elements/seo-header';
import SeoDescription from 'components/_global/layout/elements/seo-description';
import AmountSavedToast from 'components/_shared/widgets/amount-saved/toast';
import Bhag from 'components/_shared/bhag-counter';

const SideDrawer = dynamic(() => import('components/_global/side-drawer'), {
  ssr: false,
});
const ListingWidgetsWrapper = dynamic(
  () => import('components/_global/layout/widgets/widget-wrapper'),
  { ssr: false }
);

const sharingImage = resourceUrl('odo/generic_sharing_image.png', {
  width: 600,
  isAssetUrl: true,
});

type LayoutProps = PageLayoutProps & {
  children?: ReactNode;
  messages?: StickyMessageInterface[];
};

const VodapayDisabledMeta = () => {
  const isVodapayActive = useVodapayActive();
  if (isVodapayActive) return null;
  return <meta name="apple-itunes-app" content="app-id=1530905791" />;
};

const MetaAndLinks = ({
  metaTags = null,
}: {
  metaTags: LayoutProps['metaTags'];
}) => (
  <Head>
    {config.thirdParty.preFetchDomains.map(domain => (
      <Fragment key={domain}>
        <link rel="dns-prefetch" href={domain} />
        <link rel="preconnect" href={domain} crossOrigin="anonymous" />
      </Fragment>
    ))}
    <title>{metaTags?.title || config.seo.meta.siteTitle}</title>
    <meta
      name="viewport"
      content="width=device-width, initial-scale=1.0, minimum-scale=1"
    />
    {metaTags?.noIndex && <meta name="robots" content="noindex" />}
    <VodapayDisabledMeta />
    <meta name="msapplication-TileColor" content="#0093d0" />
    <meta name="theme-color" content="black" />
    <meta name="msapplication-TileImage" content="/144x144.png" />
    <link rel="icon" href="/favicon.ico" />
    <meta
      key="name.description"
      name="description"
      content={metaTags?.description || config.seo.meta.description}
    />
    <meta key="name.keywords" name="keywords" content={metaTags?.keywords} />
    <meta
      key="property.fb:app_id"
      property="fb:app_id"
      content={config.social.facebookAppId}
    />
    {!!config.social.facebookDomainVerification && (
      <meta
        name="facebook-domain-verification"
        content={config.social.facebookDomainVerification}
      />
    )}
    <meta
      key="property.og:description"
      property="og:description"
      content={metaTags?.description || config.seo.meta.description}
    />
    <meta key="property.og:image" property="og:image" content={sharingImage} />
    <meta
      key="property.og:site_name"
      property="og:site_name"
      content={config.seo.meta.siteName}
    />
    <meta
      key="property.og:title"
      property="og:title"
      content={config.seo.meta.siteTitle}
    />
    <meta
      key="property.og:url"
      property="og:url"
      content="https://www.onedayonly.co.za"
    />
    <meta
      key="property.twitter:card"
      property="twitter:card"
      content="summary_large_image"
    />
    <meta
      key="property.twitter:description"
      property="twitter:description"
      content={metaTags?.description || config.seo.meta.description}
    />
    <meta
      key="property.twitter:image:src"
      property="twitter:image:src"
      content={sharingImage}
    />
    <meta
      key="property.twitter:site"
      property="twitter:site"
      content={`@${config.social.twitterHandle}`}
    />
    <meta
      key="property.twitter:title"
      property="twitter:title"
      content={metaTags?.title || config.seo.meta.siteTitle}
    />

    {metaTags?.canonical && (
      <link key="canonical" rel="canonical" href={metaTags.canonical} />
    )}

    <link href="/manifest.json" rel="manifest" />

    {!!metaTags?.schema && (
      <script type="application/ld+json">{metaTags.schema}</script>
    )}
  </Head>
);

/**
 * NOTE: CRP requested that each page has some unique attribute that they can use for testing.
 * This is a separate component simply to contain re-renders to this component alone, instead
 * of affecting the entire layout. It has no other use than containing the data attribute.
 */
const PathData = () => {
  const { asPath } = useRouter();
  return <div data-path={asPath} />;
};

const ContentWrapper = ({ children }: { children: ReactNode }) => {
  const isNavigationBarActive = useNavigationBarActive();

  return (
    <Flex
      minHeight="100vh"
      flexDirection="column"
      mb={
        isNavigationBarActive
          ? [
              `${NAVIGATION_BAR_HEIGHT_MOBILE}px`,
              `${NAVIGATION_BAR_HEIGHT_TABLET}px`,
            ]
          : undefined
      }
    >
      <PathData />
      {children}
    </Flex>
  );
};

const Layout = ({
  children,
  messages,
  hideHeader,
  hideFooter,
  hideClock,
  showAdoHeader,
  metaTags = null,
  selectedTopTab = null,
  searchIndexName,
  headerTaglineText,
  headerTaglineTitle,
  gtmPageTitle,
  categories,
  hasScrollButton,
  hasAppDownloadWidget,
  disableHeaderShrinking,
  moreProductsFooter,
  seo,
  bhagConfigs,
  showCustomerSavingToast,
}: LayoutProps) => (
  <LayoutProviders>
    <MetaAndLinks metaTags={metaTags} />

    <SideDrawerOverlay />

    <ContentWrapper>
      {!hideHeader && (
        <ErrorBoundary>
          <Header
            messages={messages}
            selectedTopTab={selectedTopTab}
            hideClock={hideClock}
            showAdoHeader={showAdoHeader}
            searchIndexName={searchIndexName}
            headerTaglineTitle={headerTaglineTitle}
            headerTaglineText={headerTaglineText}
            categories={categories}
            disableHeaderShrinking={disableHeaderShrinking}
          />
        </ErrorBoundary>
      )}

      <div
        css={css`
          flex: 1 0 auto;
        `}
      >
        {!!seo?.header?.title && <SeoHeader {...seo.header} />}

        {children}

        {(hasScrollButton || hasAppDownloadWidget) && (
          <ListingWidgetsWrapper
            hasScrollButton={hasScrollButton}
            hasAppDownloadWidget={hasAppDownloadWidget}
          />
        )}

        {moreProductsFooter?.enabled && (
          <MoreProductsFooter {...moreProductsFooter} />
        )}

        {!!seo?.description && <SeoDescription description={seo.description} />}
      </div>

      {bhagConfigs?.showBhag && (
        <Bhag
          SSRConfigs={{
            value: bhagConfigs.value,
            incrementValue: bhagConfigs.incrementValue,
            lastUpdatedAt: bhagConfigs.lastUpdatedAt,
          }}
          showLearnMore
        />
      )}

      {!hideFooter && (
        <ErrorBoundary>
          <Footer categories={categories} />
        </ErrorBoundary>
      )}

      {!hideHeader && (
        <ErrorBoundary>
          <SideDrawer />
        </ErrorBoundary>
      )}
    </ContentWrapper>
    {showCustomerSavingToast && <AmountSavedToast />}

    <GTMAlwaysOnListener gtmPageTitle={gtmPageTitle} />
  </LayoutProviders>
);

/**
 * @see https://developers.google.com/tag-platform/gtagjs/reference
 * @see https://developers.google.com/tag-platform/gtagjs/install
 */
const Gtag = ({
  dataLayerName,
}: {
  dataLayerName: GoogleTagManagerSettings['dataLayerName'];
}) => {
  const { setClientOptions } = useContext(ApolloClientOptionsContext);
  const [initialized, setInitialized] = useState(false);
  const addGlobalStatus = useAddGlobalStatus();

  const onLoaded = useCallback(
    (gtm: GoogleTagInfo) => {
      if (!gtm.clientId || !gtm.sessionId) return;
      setClientOptions(options => ({ ...options, gtm }));
    },
    [setClientOptions]
  );

  useGoogleTagInfo({ gtagIsInitialized: initialized, onLoaded });

  return (
    <Script
      id="gtag-init"
      strategy="afterInteractive"
      onReady={() => {
        setInitialized(true);
        addGlobalStatus(GTAG_INITIALIZED);
      }}
    >
      {`function gtag(){${dataLayerName}.push(arguments);}`}
    </Script>
  );
};

const Wrapper = (props: LayoutProps) => (
  <>
    <BloomreachInit />
    {/* supposedly needs to be IMMEDIATELY after the body tag */}
    {!!config.gtm.containerId && (
      <noscript>
        <iframe
          title="gtm-noscript"
          src={`https://www.googletagmanager.com/ns.html?id=${config.gtm.containerId}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>
    )}

    <Layout {...props} />

    {/* nextjs will pick this up and decide when/where to put it in the header */}
    {!!config.gtm.containerId && (
      <>
        {/* data layer initializer (could be wrong, but pretty sure this needs to be present) */}
        {/* <Script id="gtm-data-layer" strategy="afterInteractive">
          {`window.${config.gtm.dataLayerName} = window.${config.gtm.dataLayerName} || [];`}
        </Script> */}

        {/* blocklist: for if any third-party scripts pulled via GTM break the site */}
        {/* <Script id="gtm-blocklist" strategy="afterInteractive">
          {`${config.gtm.dataLayerName}.push({'gtm.blocklist': ['nonGoogleScripts']});`}
        </Script> */}

        <Script id="gtm-sdk" strategy="beforeInteractive">
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});` +
            `var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';` +
            `j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;` +
            `f.parentNode.insertBefore(j,f);})` +
            `(window,document,'script','${config.gtm.dataLayerName}','${config.gtm.containerId}');`}
        </Script>
      </>
    )}

    {config.gtm.dataLayerName && (
      <Gtag dataLayerName={config.gtm.dataLayerName} />
    )}

    {config.netcore?.unbxdAnaytlicsScriptUrl && (
      <Script
        type="text/javascript"
        defer
        strategy="afterInteractive"
        src={config.netcore.unbxdAnaytlicsScriptUrl}
      />
    )}

    <CookieConsent />

    {/** Biz asked us to re-enable this despite the issues on XTD.
     * They've decided the risk is worth being able to make use of
     * the Convert Experiences functionality.*/}
    {/** NOTE: Config disables the script site-wide, where the SSR prop
     * disables the widget per page.*/}
    {!props.disableConvertScript &&
      !config.thirdParty.disableConvertExperiences && (
        <ConvertExperiencesWidget />
      )}
  </>
);

export default Wrapper;
